import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  PhoneCall, PhoneOff, MessageCircle, MessageSquareText, Calendar, X,
  Clock, Mail, Sparkles, Loader2, Search, Download, Mic, Square, Trash2, UserPlus, RotateCcw,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { CommissionsPanel } from "@/components/CommissionsPanel";
import { RecallsPanel } from "@/components/RecallsPanel";

const STATUSES = [
  { key: "nuovo",                 label: "Nuovo",             cls: "bg-[#F2F2EC] text-[#0A0A0A] border-[#0A0A0A]" },
  { key: "chiamato_no_risposta",  label: "Chiamato, no risp", cls: "bg-amber-50 text-amber-700 border-amber-600" },
  { key: "risposto_telefono",     label: "Risposto telefono", cls: "bg-emerald-50 text-emerald-700 border-emerald-600" },
  { key: "messaggio_inviato",     label: "Msg inviato",       cls: "bg-sky-50 text-sky-700 border-sky-600" },
  { key: "messaggio_ricevuto",    label: "Msg ricevuto",      cls: "bg-emerald-50 text-emerald-700 border-emerald-600" },
  { key: "richiama_dopo",         label: "Richiama dopo",     cls: "bg-violet-50 text-violet-700 border-violet-600" },
  { key: "appuntamento_fissato",  label: "Appuntamento ✓",    cls: "bg-[#FF5A1F] text-white border-[#FF5A1F]" },
  { key: "non_interessato",       label: "Non interessato",   cls: "bg-neutral-100 text-neutral-700 border-neutral-500" },
  { key: "non_qualificato",       label: "Non qualificato",   cls: "bg-neutral-100 text-neutral-500 border-neutral-400" },
];
const STATUS_BY_KEY = Object.fromEntries(STATUSES.map((s) => [s.key, s]));
const FINAL_STATUSES = new Set(["appuntamento_fissato", "non_interessato", "non_qualificato"]);

export default function AdminSetting() {
  const { user } = useAuth();
  const [data, setData] = useState({ items: [], counts: {} });
  const [summary, setSummary] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [waTemplate, setWaTemplate] = useState("");
  const [waModalLead, setWaModalLead] = useState(null); // { lead, prefilled }
  const [waEditorOpen, setWaEditorOpen] = useState(false);
  const [recallLead, setRecallLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includeFinal, setIncludeFinal] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest"); // newest | oldest | name
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [hideDuplicates, setHideDuplicates] = useState(true);
  const [detail, setDetail] = useState(null); // lead oggetto o null

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/setting/leads", { params: { include_final: includeFinal } });
      setData(r.data);
    } catch {
      toast.error("Errore caricamento setting");
    } finally {
      setLoading(false);
    }
  };
  const loadSummary = async () => {
    try {
      const r = await api.get("/setting/leads/summary");
      setSummary(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeFinal]);
  useEffect(() => {
    loadSummary();
    const h = () => loadSummary();
    window.addEventListener("setting-refresh", h);
    const t = setInterval(loadSummary, 30000);
    return () => { window.removeEventListener("setting-refresh", h); clearInterval(t); };
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/setting/vendors");
        setVendors(r.data?.items || []);
      } catch { /* empty */ }
    })();
    (async () => {
      try {
        const r = await api.get("/setting/wa-template");
        setWaTemplate(r.data?.text || "");
      } catch { /* empty */ }
    })();
  }, []);

  // Costruisce il messaggio WhatsApp sostituendo i placeholder
  const buildWaMessage = (lead) => {
    const setterName = user?.name || user?.email?.split("@")[0] || "team Perla Music";
    const modulo = lead?.ad_name || lead?.campagna || lead?.form || "il nostro percorso di crescita artistica";
    const nome = (lead?.artist_name || "").split(" ")[0] || "";
    return (waTemplate || "")
      .replace(/\{\{\s*nome\s*\}\}/g, nome)
      .replace(/\{\{\s*setter\s*\}\}/g, setterName)
      .replace(/\{\{\s*modulo\s*\}\}/g, modulo);
  };

  const openWhatsAppModal = (lead) => {
    setWaModalLead({ lead, prefilled: buildWaMessage(lead) });
  };

  // Contatori ricalcolati localmente (bulletproof dopo ogni azione)
  const liveCounts = useMemo(() => {
    const items = data.items || [];
    const base = { nuovo: 0, chiamato_no_risposta: 0, risposto_telefono: 0, messaggio_inviato: 0, messaggio_ricevuto: 0, richiama_dopo: 0, appuntamento_fissato: 0, non_interessato: 0, non_qualificato: 0 };
    for (const i of items) {
      const s = i.setting_status || "nuovo";
      if (base[s] !== undefined) base[s] += 1;
    }
    // "Da chiamare" = tutti quelli NON finali (nuovi + lavorazione aperta)
    const daChiamare = items.filter((i) => !FINAL_STATUSES.has(i.setting_status || "nuovo")).length;
    return { ...base, totale: items.length, da_chiamare: daChiamare };
  }, [data.items]);

  const filtered = useMemo(() => {
    let items = data.items || [];
    if (hideDuplicates) items = items.filter((i) => !i.is_duplicate);
    if (filterStatus !== "all") items = items.filter((i) => (i.setting_status || "nuovo") === filterStatus);
    if (filterPlatform !== "all") items = items.filter((i) => (i.platform || "unknown") === filterPlatform);
    if (query.trim()) {
      const q = query.toLowerCase();
      items = items.filter((i) =>
        (i.artist_name || "").toLowerCase().includes(q) ||
        (i.telefono || "").toLowerCase().includes(q) ||
        (i.email || "").toLowerCase().includes(q) ||
        (i.campagna || "").toLowerCase().includes(q)
      );
    }
    items = [...items].sort((a, b) => {
      if (sortBy === "name") return (a.artist_name || "").localeCompare(b.artist_name || "");
      const da = a.created_at || a.lead_date || "";
      const db_ = b.created_at || b.lead_date || "";
      if (sortBy === "oldest") return da.localeCompare(db_);
      return db_.localeCompare(da);
    });
    return items;
  }, [data, filterStatus, filterPlatform, hideDuplicates, query, sortBy]);

  const applyAction = async (lead, newStatus, note, extra = {}) => {
    try {
      await api.patch(`/setting/leads/${lead.id}/action`, {
        setting_status: newStatus,
        note: note ?? undefined,
        richiama_at: extra.richiama_at ?? undefined,
      });
      const prevStatus = lead.setting_status || "nuovo";
      toast.success(
        newStatus === "nuovo" ? "Stato annullato: lead tornato a Nuovo" : `${STATUS_BY_KEY[newStatus]?.label || newStatus}`,
        newStatus !== "nuovo" && prevStatus === "nuovo"
          ? { action: { label: "Annulla", onClick: () => resetToNuovo({ ...lead, setting_status: newStatus }) } }
          : undefined,
      );
      window.dispatchEvent(new Event("setting-refresh"));
      if (newStatus === "nuovo" && !includeFinal && FINAL_STATUSES.has(prevStatus)) {
        load();
        return;
      }
      // Aggiornamento locale + rimozione se diventa FINAL e non si includono i chiusi
      setData((d) => {
        const nextItems = (d.items || [])
          .map((i) => (i.id === lead.id
            ? {
                ...i,
                setting_status: newStatus,
                setting_last_action_at: new Date().toISOString(),
                setting_richiama_at: extra.richiama_at ?? i.setting_richiama_at,
              }
            : i))
          .filter((i) => (includeFinal ? true : !FINAL_STATUSES.has(i.setting_status || "nuovo")));
        return { ...d, items: nextItems };
      });
      if (detail?.id === lead.id) {
        if (!includeFinal && FINAL_STATUSES.has(newStatus)) setDetail(null);
        else setDetail({ ...detail, setting_status: newStatus });
      }
      // Dopo "No risposta" apri automaticamente il prompt WhatsApp follow-up
      if (newStatus === "chiamato_no_risposta" && (lead.telefono || "").trim()) {
        setTimeout(() => openWhatsAppModal(lead), 150);
      }
    } catch {
      toast.error("Errore salvataggio azione");
    }
  };

  const markWaSent = async (lead, undo = false) => {
    if (undo && !window.confirm("Annullare l'etichetta \"Mandato messaggio WhatsApp\"?")) return;
    try {
      const r = undo
        ? await api.delete(`/setting/leads/${lead.id}/wa-sent`)
        : await api.post(`/setting/leads/${lead.id}/wa-sent`);
      const patch = { wa_last_sent_at: r.data.wa_last_sent_at, wa_sent_count: r.data.wa_sent_count };
      patchLead(lead.id, patch);
      if (undo) toast.success("Etichetta WhatsApp annullata");
    } catch {
      toast.error("Errore salvataggio invio WhatsApp");
    }
  };

  const patchLead = (id, patch) => {
    setData((d) => ({ ...d, items: (d.items || []).map((i) => (i.id === id ? { ...i, ...patch } : i)) }));
    setDetail((det) => (det?.id === id ? { ...det, ...patch } : det));
  };

  const toggleInvalidNumber = async (lead) => {
    const value = !lead.invalid_number_at;
    try {
      const r = await api.post(`/setting/leads/${lead.id}/invalid-number`, { value });
      patchLead(lead.id, { invalid_number_at: r.data.invalid_number_at });
      toast.success(value ? "Segnato: numero non valido" : "Numero di nuovo valido");
    } catch {
      toast.error("Errore salvataggio");
    }
  };

  const resetToNuovo = (lead) => {
    const label = STATUS_BY_KEY[lead.setting_status]?.label || lead.setting_status;
    if (!window.confirm(`Riportare "${lead.artist_name || "questo lead"}" da "${label}" a "Nuovo"?\nLe note restano, le caselle sul foglio Google vengono tolte.`)) return;
    applyAction(lead, "nuovo");
  };

  const assignVendor = async (lead, vendorId) => {
    try {
      const r = await api.post(`/setting/leads/${lead.id}/assign`, { vendor_id: vendorId });
      toast.success(`Assegnato a ${r.data.assigned_vendor_name}`);
      window.dispatchEvent(new Event("setting-refresh"));
      setData((d) => ({
        ...d,
        items: (d.items || []).map((i) =>
          i.id === lead.id
            ? { ...i, assigned_vendor_id: r.data.assigned_vendor_id, assigned_vendor_name: r.data.assigned_vendor_name }
            : i
        ),
      }));
      if (detail?.id === lead.id) {
        setDetail({ ...detail, assigned_vendor_id: r.data.assigned_vendor_id, assigned_vendor_name: r.data.assigned_vendor_name });
      }
    } catch {
      toast.error("Errore assegnazione");
    }
  };

  const addTextNote = async (lead, text) => {
    const note = await api.post(`/setting/leads/${lead.id}/text-note`, { text });
    setData((d) => ({
      ...d,
      items: (d.items || []).map((i) => (i.id === lead.id ? { ...i, notes: [...(i.notes || []), note.data] } : i)),
    }));
    if (detail?.id === lead.id) setDetail({ ...detail, notes: [...(detail.notes || []), note.data] });
    return note.data;
  };

  const addVoiceNote = async (lead, blob) => {
    const fd = new FormData();
    fd.append("audio", blob, "note.webm");
    const r = await api.post(`/setting/leads/${lead.id}/voice-note`, fd);
    setData((d) => ({
      ...d,
      items: (d.items || []).map((i) => (i.id === lead.id ? { ...i, notes: [...(i.notes || []), r.data] } : i)),
    }));
    if (detail?.id === lead.id) setDetail({ ...detail, notes: [...(detail.notes || []), r.data] });
    return r.data;
  };

  const deleteNote = async (lead, noteId) => {
    await api.delete(`/setting/leads/${lead.id}/notes/${noteId}`);
    setData((d) => ({
      ...d,
      items: (d.items || []).map((i) => (i.id === lead.id ? { ...i, notes: (i.notes || []).filter((n) => n.id !== noteId) } : i)),
    }));
    if (detail?.id === lead.id) setDetail({ ...detail, notes: (detail.notes || []).filter((n) => n.id !== noteId) });
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="setting-page">
      <header className="mb-6">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F]">
          <PhoneCall size={14} /> Setting · Prima call
        </div>
        <div className="mt-2 flex items-start justify-between gap-4 flex-wrap">
          <h1 className="font-display text-4xl md:text-5xl uppercase text-[#0A0A0A]">
            Qualifica lead
          </h1>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setWaEditorOpen(true)}
              className="px-3 py-2 border-2 border-emerald-700 bg-white text-emerald-700 text-xs uppercase tracking-widest font-bold hover:bg-emerald-700 hover:text-white whitespace-nowrap"
              data-testid="edit-wa-template-btn"
              title="Modifica il testo del messaggio WhatsApp di follow-up"
            >
              📱 Modifica messaggio WhatsApp
            </button>
            <button
              onClick={async () => {
                try {
                  const res = await api.get("/setting/leads/export.xlsx", { responseType: "blob" });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `lead_da_chiamare_${new Date().toISOString().slice(0,10)}.xlsx`;
                  a.click();
                  window.URL.revokeObjectURL(url);
                  toast.success("Excel scaricato (esclusi i non interessati)");
                } catch { toast.error("Errore export Excel"); }
              }}
              className="btn-cta whitespace-nowrap"
              data-testid="export-xlsx-btn"
              title="Scarica Excel con tutti i lead eccetto quelli segnati come 'non interessati'"
            >
              <Download size={14} /> Scarica lista lead (Excel)
            </button>
            {user?.role === "admin" && (
              <button
                onClick={async () => {
                  try {
                    // Step 1: dry-run per mostrare il piano
                    toast.info("Analizzo il foglio…");
                    const dry = await api.post("/manager/meta-leads/align-checkboxes?dry_run=true");
                    const d = dry.data || {};
                    if (!d.ok) {
                      toast.error(`Errore: ${d.error || "sconosciuto"}`);
                      return;
                    }
                    const msg = `Tab: ${d.tab}\nColonne: ${(d.columns || []).join(", ")}\n\n` +
                                `✓ Checkbox da spuntare: ${d.true_cells}\n` +
                                `☐ Checkbox vuote: ${d.false_cells + d.empty_cells}\n` +
                                `Testo non booleano preservato: ${d.preserved_text}\n\n` +
                                `Procedo con l'allineamento? (celle vuote restano vuote, testo non booleano non viene toccato)`;
                    if (!window.confirm(msg)) return;
                    toast.info("Allineo le checkbox…");
                    const run = await api.post("/manager/meta-leads/align-checkboxes?dry_run=false");
                    const r = run.data || {};
                    if (r.ok) {
                      toast.success(`✓ Allineate ${r.true_cells + r.false_cells + r.empty_cells} celle su ${(r.columns || []).length} colonne`);
                    } else {
                      toast.error(`Errore allineamento: ${r.error || "sconosciuto"}`);
                    }
                  } catch (e) {
                    toast.error(`Errore: ${e?.response?.data?.detail || e.message}`);
                  }
                }}
                className="px-3 py-2 border-2 border-sky-700 bg-white text-sky-700 text-xs uppercase tracking-widest font-bold hover:bg-sky-700 hover:text-white whitespace-nowrap"
                data-testid="align-checkboxes-btn"
                title="One-shot: converte le colonne Chiamato?/Risposto/Risposta Msg/Msg senza risposta/Appuntamento?/Diventato Cliente? in vere checkbox. Preserva lo stato esistente."
              >
                ☑ Allinea Checkbox
              </button>
            )}
          </div>
        </div>
        <p className="mt-2 text-sm text-[#0A0A0A]/60 max-w-3xl">
          Lead Meta appena arrivati dal Google Sheet, con TUTTE le risposte del form in sola lettura (clic sul nome per il dettaglio).
          Qui chiami, mandi messaggi e qualifichi. Quando fissi l'appuntamento con il venditore, il lead esce da questa lista e torna nel calendario.
        </p>
        {/* Range date della lista correntemente filtrata */}
        {(() => {
          const dates = (data.items || []).map((i) => (i.lead_date || "").slice(0, 10)).filter(Boolean).sort();
          if (!dates.length) return null;
          const fmt = (s) => {
            try { return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return s; }
          };
          return (
            <div className="mt-3 inline-block px-3 py-1.5 border-2 border-[#0A0A0A] bg-[#F2F2EC] text-xs" data-testid="date-range">
              <b data-testid="da-chiamare-counter">{liveCounts.da_chiamare}</b> lead da chiamare — ricevuti dal <b>{fmt(dates[0])}</b> al <b>{fmt(dates[dates.length - 1])}</b>
            </div>
          );
        })()}
      </header>

      {/* Riepilogo COMPLETO campagna setting (sempre include anche i chiusi) */}
      <RecallsPanel items={data.items} onOpenDetail={(l) => setDetail(l)} onPlanRecall={(l) => setRecallLead(l)} />
      <CommissionsPanel
        scope="setter"
        title={user?.role === "admin" ? "Provvigioni setting 5% · tutti i setter" : "Le mie provvigioni setting (5%)"}
      />
      {summary && (
        <div className="mb-6 border-4 border-[#0A0A0A] bg-white p-5" data-testid="setting-summary">
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">
              📊 Quadro campagna · dal {summary.range.start || "inizio"} ad oggi
            </div>
            <div className="text-xs text-[#0A0A0A]/60 text-right">
              {summary.duplicati > 0 && (
                <div className="text-[11px] mt-0.5">
                  <b className="text-emerald-700">{summary.totale} unici</b>
                  {" · "}
                  <span className="text-neutral-500">{summary.duplicati} duplicati rilevati</span>
                </div>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center mb-3">
            <SummaryTile label="Nuovi" value={summary.aggregates.nuovi} color="neutral" testid="sum-nuovi" />
            <SummaryTile label="Hanno risposto" value={summary.aggregates.hanno_risposto} color="emerald" sub={summary.aggregates.chiamati ? `${summary.aggregates.tasso_risposta_pct}% dei chiamati` : ""} testid="sum-hanno-risposto" />
            <SummaryTile label="Appuntamenti presi" value={summary.aggregates.appuntamenti_presi} color="orange" sub={`${summary.aggregates.tasso_appuntamenti_pct}% dei lead`} testid="sum-appuntamenti" />
            <SummaryTile label="Assegnati a venditori" value={summary.aggregates.assegnati_a_venditore} color="violet" testid="sum-assegnati" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center text-xs">
            <MiniTile label="Senza risposta" value={summary.aggregates.senza_risposta} />
            <MiniTile label="Msg inviati" value={summary.aggregates.msg_inviati} />
            <MiniTile label="Msg ricevuti" value={summary.aggregates.msg_ricevuti} />
            <MiniTile label="Da richiamare" value={summary.aggregates.da_richiamare} />
          </div>
          {(summary.per_vendor || []).length > 0 && (
            <div className="mt-4 border-t-2 border-[#0A0A0A]/10 pt-3">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60 mb-2">
                👥 Ripartizione per venditore
              </div>
              <div className="grid md:grid-cols-2 gap-2">
                {summary.per_vendor.map((v) => (
                  <div key={v.vendor_id} className="flex items-center justify-between border-2 border-[#0A0A0A]/20 bg-[#F2F2EC] px-3 py-2 text-xs">
                    <b>{v.vendor_name}</b>
                    <div className="flex gap-4 text-[#0A0A0A]/70">
                      <span>Assegnati: <b className="text-[#0A0A0A]">{v.assegnati}</b></span>
                      <span>Appunt.: <b className="text-[#FF5A1F]">{v.appuntamenti}</b></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* KPI counts — 3 badge: Nuovi · Senza risposta · Appuntamenti */}
      <div className="grid grid-cols-3 gap-2 mb-6" data-testid="setting-counts">
        {[
          { key: "nuovo",                label: "Nuovi" },
          { key: "chiamato_no_risposta", label: "Senza risposta" },
          { key: "appuntamento_fissato", label: "Appuntamenti" },
        ].map((s) => (
          <button
            key={s.key}
            onClick={() => setFilterStatus(filterStatus === s.key ? "all" : s.key)}
            className={`p-3 border-2 text-left transition ${
              filterStatus === s.key ? "border-[#FF5A1F] bg-[#FF5A1F]/10" : "border-[#0A0A0A]/20 hover:border-[#0A0A0A]"
            }`}
            data-testid={`count-${s.key}`}
          >
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">{s.label}</div>
            <div className="font-display text-3xl text-[#0A0A0A] mt-1">{liveCounts[s.key] ?? 0}</div>
          </button>
        ))}
      </div>

      {/* Filtri + search */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A0A0A]/40" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome, telefono, email, campagna…"
            className="w-full pl-9 pr-3 py-2 border-2 border-[#0A0A0A] text-sm focus:outline-none focus:border-[#FF5A1F]"
            data-testid="setting-search"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border-2 border-[#0A0A0A] px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#FF5A1F]"
          data-testid="setting-filter"
        >
          <option value="all">Tutti gli stati</option>
          {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="border-2 border-[#0A0A0A] px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#FF5A1F]"
          data-testid="setting-sort"
          title="Ordina i lead"
        >
          <option value="newest">Più nuovi in cima (default)</option>
          <option value="oldest">Più vecchi in cima</option>
          <option value="name">Nome A→Z</option>
        </select>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={includeFinal}
            onChange={(e) => setIncludeFinal(e.target.checked)}
            data-testid="include-final"
          />
          Includi chiusi (archivio)
        </label>
        <label className="flex items-center gap-2 text-xs cursor-pointer" title="Nasconde i lead che hanno lo stesso telefono di uno già arrivato prima">
          <input
            type="checkbox"
            checked={hideDuplicates}
            onChange={(e) => setHideDuplicates(e.target.checked)}
            data-testid="hide-duplicates"
          />
          Nascondi duplicati{data.counts?.duplicates ? ` (${data.counts.duplicates})` : ""}
        </label>
        <div className="text-[11px] uppercase tracking-widest text-[#0A0A0A]/50 ml-auto">
          {filtered.length} / {liveCounts.totale}
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-sm text-[#0A0A0A]/50">
          <Loader2 size={20} className="animate-spin inline" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border-2 border-dashed border-[#0A0A0A]/30 p-10 text-center text-sm text-[#0A0A0A]/50">
          Nessun lead corrisponde ai filtri.
        </div>
      ) : (
        <div className="space-y-2" data-testid="setting-list">
          {filtered.map((lead) => (
            <LeadRow
              key={lead.id}
              lead={lead}
              vendors={vendors}
              onAction={(status, note) => applyAction(lead, status, note)}
              onAssign={(vendorId) => assignVendor(lead, vendorId)}
              onOpenDetail={() => setDetail(lead)}
              onOpenWhatsApp={() => openWhatsAppModal(lead)}
              onPlanRecall={() => setRecallLead(lead)}
              onReset={() => resetToNuovo(lead)}
              onUndoWa={() => markWaSent(lead, true)}
              onInvalidNumber={() => toggleInvalidNumber(lead)}
            />
          ))}
        </div>
      )}

      {detail && (
        <LeadDetailModal
          lead={detail}
          vendors={vendors}
          onClose={() => setDetail(null)}
          onAction={(status, note) => applyAction(detail, status, note)}
          onAssign={(vendorId) => assignVendor(detail, vendorId)}
          onAddTextNote={(text) => addTextNote(detail, text)}
          onAddVoiceNote={(blob) => addVoiceNote(detail, blob)}
          onDeleteNote={(noteId) => deleteNote(detail, noteId)}
          onOpenWhatsApp={() => openWhatsAppModal(detail)}
        />
      )}

      {waModalLead && (
        <WhatsAppFollowupModal
          lead={waModalLead.lead}
          initialText={waModalLead.prefilled}
          onClose={() => setWaModalLead(null)}
          onSent={() => markWaSent(waModalLead.lead)}
          onEditTemplate={() => { setWaEditorOpen(true); setWaModalLead(null); }}
        />
      )}

      {waEditorOpen && (
        <WhatsAppTemplateEditor
          currentText={waTemplate}
          onClose={() => setWaEditorOpen(false)}
          onSaved={(text) => { setWaTemplate(text); setWaEditorOpen(false); toast.success("Template salvato"); }}
        />
      )}

      {recallLead && (
        <RecallPlannerModal
          lead={recallLead}
          onClose={() => setRecallLead(null)}
          onConfirm={async (iso, note) => {
            await applyAction(recallLead, "richiama_dopo", note || undefined, { richiama_at: iso });
            setRecallLead(null);
          }}
        />
      )}
    </div>
  );
}

function LeadRow({ lead, vendors, onAction, onAssign, onOpenDetail, onOpenWhatsApp, onPlanRecall, onReset, onUndoWa, onInvalidNumber }) {
  const st = STATUS_BY_KEY[lead.setting_status] || STATUS_BY_KEY.nuovo;
  const lastAction = lead.setting_last_action_at
    ? new Date(lead.setting_last_action_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    : "—";
  const leadDate = lead.created_at
    ? new Date(lead.created_at).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit" })
    : "—";
  return (
    <div className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-4 flex flex-col gap-3" data-testid={`lead-${lead.id}`}>
      <div className="w-full min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onOpenDetail}
            className="font-display text-xl uppercase text-[#0A0A0A] hover:text-[#FF5A1F] text-left break-words"
            data-testid={`lead-name-${lead.id}`}
          >
            {lead.artist_name || "—"}
          </button>
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border-2 ${st.cls}`}>
            {st.label}
          </span>
          {lead.assigned_vendor_name && (
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border-2 border-[#FF5A1F] text-[#FF5A1F] bg-orange-50">
              👤 {lead.assigned_vendor_name}
            </span>
          )}
          {lead.wa_last_sent_at && (
            <span
              className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border-2 border-emerald-700 text-emerald-800 bg-emerald-50"
              title={`Messaggi WhatsApp inviati: ${lead.wa_sent_count || 1}`}
              data-testid={`lead-wa-sent-${lead.id}`}
            >
              ✓ Mandato messaggio WhatsApp · {new Date(lead.wa_last_sent_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}
              {(lead.wa_sent_count || 1) > 1 ? ` (${lead.wa_sent_count}×)` : ""}
              <button
                onClick={onUndoWa}
                className="ml-1.5 px-1 hover:bg-emerald-700 hover:text-white transition-colors"
                title="Annulla etichetta (es. numero non su WhatsApp)"
                data-testid={`lead-wa-sent-undo-${lead.id}`}
              >
                ✕
              </button>
            </span>
          )}
          {lead.invalid_number_at && (
            <span
              className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border-2 border-red-700 text-white bg-red-600"
              data-testid={`lead-invalid-number-${lead.id}`}
            >
              ⚠ Numero non valido
            </span>
          )}
          {lead.is_duplicate && (
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border-2 border-neutral-500 bg-neutral-100 text-neutral-700" title={`Stesso telefono di ${lead.duplicate_of_name || "lead precedente"}`}>
              📎 Duplicato
            </span>
          )}
          {/* Provenienza compatta nella riga: platform + ad */}
          {lead.platform && (
            <span
              className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border-2 ${
                lead.platform === "fb" ? "border-blue-700 text-blue-700 bg-blue-50"
                : lead.platform === "ig" ? "border-pink-600 text-pink-600 bg-pink-50"
                : "border-neutral-500 text-neutral-700 bg-neutral-100"
              }`}
              title={`Piattaforma Meta: ${lead.platform}${lead.is_organic ? " · organico" : " · ads"}`}
            >
              {lead.platform === "fb" ? "📘 Facebook" : lead.platform === "ig" ? "📸 Instagram" : lead.platform.toUpperCase()}
              {lead.is_organic ? " · organico" : ""}
            </span>
          )}
          {lead.ad_name && (
            <span className="text-[10px] text-[#0A0A0A]/60 uppercase tracking-widest truncate max-w-[280px]" title={lead.ad_name}>
              📣 {lead.ad_name}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-3 mt-2 text-xs text-[#0A0A0A]/70">
          {lead.telefono && (
            <a href={`tel:${lead.telefono}`} className="hover:text-[#FF5A1F] flex items-center gap-1" data-testid={`lead-phone-${lead.id}`}>
              <PhoneCall size={12} /> {lead.telefono}
            </a>
          )}
          {lead.telefono && (
            <a
              href={`https://wa.me/${lead.telefono.replace(/\D/g, "")}`}
              target="_blank" rel="noreferrer"
              className="hover:text-emerald-600 flex items-center gap-1"
              data-testid={`lead-whatsapp-${lead.id}`}
            >
              <MessageCircle size={12} /> WhatsApp
            </a>
          )}
          {lead.email && (
            <a href={`mailto:${lead.email}`} className="hover:text-[#FF5A1F] flex items-center gap-1">
              <Mail size={12} /> {lead.email}
            </a>
          )}
          <span className="text-[#0A0A0A]/40">
            <Clock size={10} className="inline mb-0.5" /> ricevuto {leadDate}
          </span>
          {lead.setting_last_action_at && (
            <span className="text-[#0A0A0A]/40">
              ultima azione {lastAction}
            </span>
          )}
          {(lead.notes || []).length > 0 && (
            <span className="text-[#FF5A1F] font-bold">
              📝 {(lead.notes || []).length} nota/e
            </span>
          )}
          {lead.setting_status === "richiama_dopo" && lead.setting_richiama_at && (
            <span className="text-violet-700 font-bold" title="Appuntamento di richiamo pianificato">
              ⏰ {new Date(lead.setting_richiama_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-[#0A0A0A]/10">
      {/* Assegna venditore */}
      {vendors.length > 0 && (
        <select
          value={lead.assigned_vendor_id || ""}
          onChange={(e) => { if (e.target.value) onAssign(e.target.value); }}
          className="text-[10px] uppercase tracking-widest font-bold border-2 border-[#0A0A0A] px-2 py-1 bg-white shrink-0"
          data-testid={`assign-vendor-${lead.id}`}
          title="Assegna al venditore"
        >
          <option value="">👤 Assegna…</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
      )}

      {/* Azioni rapide */}
      <div className="flex flex-wrap gap-1">
        <ActionBtn
          icon={<PhoneOff size={11} />}
          label="No risp"
          color="amber"
          onClick={() => onAction("chiamato_no_risposta")}
          testid={`act-noresp-${lead.id}`}
        />
        <ActionBtn
          icon={<PhoneCall size={11} />}
          label="Risposto"
          color="emerald"
          onClick={() => onAction("risposto_telefono")}
          testid={`act-answered-${lead.id}`}
        />
        <ActionBtn
          icon={<MessageSquareText size={11} />}
          label="Msg inv."
          color="sky"
          onClick={() => onAction("messaggio_inviato")}
          testid={`act-msgsent-${lead.id}`}
        />
        {lead.telefono && (
          <ActionBtn
            icon={<MessageCircle size={11} />}
            label="WA follow-up"
            color="emerald"
            onClick={onOpenWhatsApp}
            testid={`act-wa-${lead.id}`}
          />
        )}
        <ActionBtn
          icon={<MessageCircle size={11} />}
          label="Msg ric."
          color="emerald"
          onClick={() => onAction("messaggio_ricevuto")}
          testid={`act-msgrecv-${lead.id}`}
        />
        <ActionBtn
          icon={<Clock size={11} />}
          label="Richiama"
          color="violet"
          onClick={onPlanRecall}
          testid={`act-recall-${lead.id}`}
        />
        <ActionBtn
          icon={<Calendar size={11} />}
          label="Appunt."
          color="orange"
          onClick={() => onAction("appuntamento_fissato")}
          testid={`act-apt-${lead.id}`}
        />
        <ActionBtn
          icon={<X size={11} />}
          label="Scarta"
          color="neutral"
          onClick={() => {
            if (window.confirm("Marcare come 'non interessato'?")) onAction("non_interessato");
          }}
          testid={`act-dismiss-${lead.id}`}
        />
        <ActionBtn
          icon={<PhoneOff size={11} />}
          label={lead.invalid_number_at ? "Numero valido" : "Numero non valido"}
          color="red"
          onClick={onInvalidNumber}
          testid={`act-invalid-number-${lead.id}`}
        />
        {(lead.setting_status || "nuovo") !== "nuovo" && (
          <ActionBtn
            icon={<RotateCcw size={11} />}
            label="Annulla stato"
            color="dark"
            onClick={onReset}
            testid={`act-reset-${lead.id}`}
          />
        )}
      </div>
      </div>
    </div>
  );
}

function ActionBtn({ icon, label, color, onClick, testid }) {
  const palette = {
    amber:   "border-amber-500 text-amber-700 hover:bg-amber-50",
    emerald: "border-emerald-600 text-emerald-700 hover:bg-emerald-50",
    sky:     "border-sky-500 text-sky-700 hover:bg-sky-50",
    orange:  "border-[#FF5A1F] text-[#FF5A1F] hover:bg-orange-50",
    violet:  "border-violet-600 text-violet-700 hover:bg-violet-50",
    neutral: "border-neutral-400 text-neutral-600 hover:bg-neutral-50",
    dark:    "border-[#0A0A0A] bg-[#0A0A0A] text-white hover:bg-neutral-800",
    red:     "border-red-600 text-red-700 hover:bg-red-50",
  }[color] || "border-[#0A0A0A] text-[#0A0A0A]";
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widest font-bold border-2 ${palette}`}
      data-testid={testid}
      title={label}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// VoiceRecorder — identico pattern del Mio Giorno (MediaRecorder → blob → upload)
// ---------------------------------------------------------------------------
function VoiceRecorder({ onRecorded }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setUploading(true);
        try {
          await onRecorded(blob);
          toast.success("Nota vocale trascritta");
        } catch {
          toast.error("Errore trascrizione");
        } finally {
          setUploading(false);
        }
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch {
      toast.error("Impossibile accedere al microfono");
    }
  };

  const stop = () => {
    if (recRef.current && recording) {
      recRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
  const ss = String(elapsed % 60).padStart(2, "0");

  if (uploading) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 border-2 border-[#FF5A1F] bg-orange-50 text-xs">
        <Loader2 size={14} className="animate-spin text-[#FF5A1F]" />
        <span>Trascrizione in corso…</span>
      </div>
    );
  }
  return (
    <button
      onClick={recording ? stop : start}
      className={`flex items-center gap-2 px-3 py-2 border-2 text-xs uppercase tracking-widest font-bold ${
        recording ? "border-red-600 bg-red-50 text-red-700 animate-pulse" : "border-[#0A0A0A] bg-white text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
      }`}
      data-testid="voice-record-btn"
      type="button"
    >
      {recording ? <Square size={14} /> : <Mic size={14} />}
      {recording ? `Stop (${mm}:${ss})` : "Dettatura vocale"}
    </button>
  );
}

function LeadDetailModal({ lead, vendors, onClose, onAction, onAssign, onAddTextNote, onAddVoiceNote, onDeleteNote, onOpenWhatsApp }) {
  const [note, setNote] = useState("");
  const [newStatus, setNewStatus] = useState(lead.setting_status || "nuovo");
  const [saving, setSaving] = useState(false);
  const [savingNote, setSavingNote] = useState(false);

  const saveAction = async () => {
    setSaving(true);
    await onAction(newStatus, note || undefined);
    setNote("");
    setSaving(false);
  };

  const saveTextNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      await onAddTextNote(note.trim());
      setNote("");
      toast.success("Nota salvata");
    } catch {
      toast.error("Errore salvataggio nota");
    } finally {
      setSavingNote(false);
    }
  };

  const handleVoice = async (blob) => {
    const n = await onAddVoiceNote(blob);
    // Preimposta la textarea con il testo trascritto così l'utente può correggerlo e salvare come nota status
    if (n?.text) setNote(n.text);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#FAFAF7] border-4 border-[#0A0A0A] max-w-3xl w-full my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#FAFAF7]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FAFAF7]/60">Dettaglio lead</div>
            <div className="font-display text-2xl uppercase">{lead.artist_name}</div>
          </div>
          <button onClick={onClose} className="text-[#FAFAF7]/60 hover:text-[#FF5A1F]">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          {/* Dati Meta */}
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            {lead.telefono && <div><b>📞 Telefono:</b> <a href={`tel:${lead.telefono}`} className="text-[#FF5A1F]">{lead.telefono}</a></div>}
            {lead.email && <div><b>✉️ Email:</b> <a href={`mailto:${lead.email}`} className="text-[#FF5A1F]">{lead.email}</a></div>}
            {lead.ascoltatori_spotify && <div><b>🎧 Ascoltatori Spotify:</b> {lead.ascoltatori_spotify}</div>}
            {lead.investimento_storico && <div><b>💰 Investimento storico:</b> {lead.investimento_storico}</div>}
          </div>

          {lead.telefono && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={onOpenWhatsApp}
                className="flex items-center gap-2 px-3 py-2 border-2 border-emerald-700 bg-white text-emerald-700 text-xs uppercase tracking-widest font-bold hover:bg-emerald-700 hover:text-white"
                data-testid="detail-wa-followup"
              >
                <MessageCircle size={14} /> Manda follow-up WhatsApp
              </button>
            </div>
          )}

          {/* Provenienza Meta — da dove arriva il lead */}
          {(lead.platform || lead.campagna || lead.ad_name || lead.form) && (
            <div className="border-2 border-blue-700 bg-blue-50/50 p-3 space-y-1 text-xs">
              <div className="text-[10px] uppercase tracking-widest font-bold text-blue-900 mb-2">
                📊 Da dove arriva questo lead
              </div>
              {lead.platform && (
                <div>
                  <b>Piattaforma:</b>{" "}
                  <span className="font-bold">
                    {lead.platform === "fb" ? "📘 Facebook" : lead.platform === "ig" ? "📸 Instagram" : lead.platform.toUpperCase()}
                  </span>
                  {" · "}
                  <span className="italic text-[#0A0A0A]/60">
                    {lead.is_organic ? "lead organico" : "da inserzione a pagamento"}
                  </span>
                </div>
              )}
              {lead.campagna && <div><b>Campagna:</b> {lead.campagna}</div>}
              {lead.adset && <div><b>Adset:</b> {lead.adset}</div>}
              {lead.ad_name && <div><b>Inserzione:</b> {lead.ad_name}</div>}
              {lead.form && <div><b>Modulo (form):</b> {lead.form}</div>}
            </div>
          )}
          {(lead.pain_point || lead.obiettivo || lead.aspettative) && (
            <div className="border-2 border-[#FF5A1F] bg-orange-50 p-3 space-y-2 text-sm">
              {lead.pain_point && <div><b>🩹 Pain:</b> {lead.pain_point}</div>}
              {lead.obiettivo && <div><b>🎯 Obiettivo:</b> {lead.obiettivo}</div>}
              {lead.disposizione_investire && <div><b>💶 Disposizione a investire:</b> {lead.disposizione_investire}</div>}
              {lead.aspettative && <div><b>💭 Aspettative:</b> {lead.aspettative}</div>}
            </div>
          )}

          {/* Assegnazione venditore */}
          {vendors && vendors.length > 0 && (
            <div className="border-2 border-[#0A0A0A] p-4 bg-white">
              <div className="text-[10px] uppercase tracking-widest font-bold mb-2 text-[#FF5A1F]">
                <UserPlus size={12} className="inline mb-0.5" /> Assegna al venditore
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <select
                  value={lead.assigned_vendor_id || ""}
                  onChange={(e) => { if (e.target.value) onAssign(e.target.value); }}
                  className="flex-1 min-w-[200px] border-2 border-[#0A0A0A] p-2 text-sm bg-white"
                  data-testid="detail-vendor-select"
                >
                  <option value="">— Seleziona venditore —</option>
                  {vendors.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
                {lead.assigned_vendor_name && (
                  <span className="text-xs text-[#0A0A0A]/60">
                    Attualmente: <b className="text-[#FF5A1F]">{lead.assigned_vendor_name}</b>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Note (vocali + testuali) */}
          <div className="border-2 border-[#0A0A0A] p-4 bg-white">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">
                📝 Note sul lead
              </div>
              <VoiceRecorder onRecorded={handleVoice} />
            </div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Scrivi una nota qui, oppure usa la dettatura vocale per trascrivere automaticamente ciò che vuoi ricordare della chiamata…"
              className="w-full border-2 border-[#0A0A0A] p-2 text-sm resize-none mb-2"
              data-testid="detail-note"
            />
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={saveTextNote}
                disabled={savingNote || !note.trim()}
                className="px-3 py-2 border-2 border-[#0A0A0A] bg-white text-xs uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:text-white disabled:opacity-50"
                data-testid="save-text-note"
              >
                {savingNote ? <Loader2 size={14} className="animate-spin inline" /> : "💾 Salva nota"}
              </button>
            </div>

            {/* Elenco note esistenti */}
            {(lead.notes || []).length > 0 && (
              <div className="mt-4 space-y-2 max-h-64 overflow-y-auto">
                {[...(lead.notes || [])].reverse().map((n) => (
                  <div key={n.id} className="border-l-4 border-[#FF5A1F] bg-[#F2F2EC] p-2 text-xs">
                    <div className="flex justify-between gap-2 items-start">
                      <div className="flex-1">
                        {n.audio_path && <div className="text-[9px] uppercase tracking-widest text-[#FF5A1F] mb-1">🎤 Vocale</div>}
                        <div className="text-[#0A0A0A]/80 whitespace-pre-wrap">{n.text || <i className="text-[#0A0A0A]/40">(nessuna trascrizione)</i>}</div>
                        <div className="text-[9px] text-[#0A0A0A]/40 mt-1 uppercase tracking-widest">
                          {new Date(n.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                          {n.by ? ` · ${n.by}` : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => { if (window.confirm("Elimina questa nota?")) onDeleteNote(n.id); }}
                        className="text-[#0A0A0A]/40 hover:text-red-600"
                        title="Elimina nota"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cambio stato */}
          <div className="border-2 border-[#0A0A0A] p-4 bg-white">
            <div className="text-[10px] uppercase tracking-widest font-bold mb-2 text-[#FF5A1F]">
              Cambia stato lead
            </div>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="w-full border-2 border-[#0A0A0A] p-2 text-sm bg-white mb-2"
              data-testid="detail-status-select"
            >
              {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <button
              onClick={saveAction}
              disabled={saving}
              className="btn-cta disabled:opacity-60"
              data-testid="detail-save"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
              Salva stato
            </button>
          </div>

          {/* Timeline azioni setter */}
          {(lead.setting_log || []).length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60 mb-2">
                Timeline azioni
              </div>
              <div className="space-y-2">
                {[...(lead.setting_log || [])].reverse().map((e, i) => (
                  <div key={i} className="border-l-4 border-[#FF5A1F] bg-[#F2F2EC] p-2 text-xs">
                    <div className="flex justify-between gap-2">
                      <b>{STATUS_BY_KEY[e.status]?.label || e.status}</b>
                      <span className="text-[#0A0A0A]/50">
                        {new Date(e.at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                      </span>
                    </div>
                    {e.note && <div className="text-[#0A0A0A]/70 mt-1">{e.note}</div>}
                    {e.by && <div className="text-[9px] text-[#0A0A0A]/40 mt-1 uppercase tracking-widest">by {e.by}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WhatsAppFollowupModal — anteprima + modifica messaggio prima dell'invio su wa.me
// ---------------------------------------------------------------------------
function WhatsAppFollowupModal({ lead, initialText, onClose, onSent, onEditTemplate }) {
  const [text, setText] = React.useState(initialText || "");
  const openWhatsApp = () => {
    const digits = (lead.telefono || "").replace(/\D/g, "");
    if (!digits) { toast.error("Numero di telefono mancante"); return; }
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
    toast.success("WhatsApp aperto");
    onSent?.();
    onClose();
  };
  const copyOnly = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Messaggio copiato"); }
    catch { toast.error("Copia non disponibile"); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#FAFAF7] border-4 border-emerald-700 max-w-2xl w-full my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-emerald-700 bg-emerald-700 text-white">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/70">Follow-up WhatsApp</div>
            <div className="font-display text-2xl uppercase">📱 {lead.artist_name}</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-xs text-[#0A0A0A]/60">
            Al numero <b className="text-[#0A0A0A]">{lead.telefono}</b>. Il messaggio è già compilato con nome, setter e modulo — puoi correggerlo prima di aprire WhatsApp.
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={10}
            className="w-full border-2 border-[#0A0A0A] p-3 text-sm resize-none font-mono"
            data-testid="wa-message-text"
          />
          <div className="flex gap-2 flex-wrap justify-between items-center">
            <button
              onClick={onEditTemplate}
              className="text-xs underline text-[#0A0A0A]/60 hover:text-emerald-700"
              data-testid="wa-edit-template"
            >
              ✏️ Modifica template predefinito
            </button>
            <div className="flex gap-2">
              <button
                onClick={copyOnly}
                className="px-3 py-2 border-2 border-[#0A0A0A] bg-white text-xs uppercase tracking-widest font-bold hover:bg-[#0A0A0A] hover:text-white"
                data-testid="wa-copy"
              >
                Copia
              </button>
              <button
                onClick={openWhatsApp}
                className="px-4 py-2 border-2 border-emerald-700 bg-emerald-700 text-white text-xs uppercase tracking-widest font-bold hover:bg-emerald-800"
                data-testid="wa-open"
              >
                <MessageCircle size={14} className="inline mr-1" /> Apri WhatsApp
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// WhatsAppTemplateEditor — editor del template persistito su backend
// ---------------------------------------------------------------------------
function WhatsAppTemplateEditor({ currentText, onClose, onSaved }) {
  const [text, setText] = React.useState(currentText || "");
  const [defaultText, setDefaultText] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => {
    (async () => {
      try { const r = await api.get("/setting/wa-template"); setDefaultText(r.data?.default || ""); } catch { /* empty */ }
    })();
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/setting/wa-template", { text });
      onSaved(r.data?.text || text);
    } catch { toast.error("Errore salvataggio template"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#FAFAF7] border-4 border-[#0A0A0A] max-w-2xl w-full my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-[#0A0A0A] bg-[#0A0A0A] text-[#FAFAF7]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FAFAF7]/60">Impostazioni WhatsApp</div>
            <div className="font-display text-2xl uppercase">Template follow-up</div>
          </div>
          <button onClick={onClose} className="text-[#FAFAF7]/60 hover:text-emerald-400"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-3">
          <div className="text-xs text-[#0A0A0A]/70">
            Usa <code className="bg-emerald-50 px-1 border border-emerald-700">{"{{ nome }}"}</code> (nome del lead),{" "}
            <code className="bg-emerald-50 px-1 border border-emerald-700">{"{{ setter }}"}</code> (chi lo manda),{" "}
            <code className="bg-emerald-50 px-1 border border-emerald-700">{"{{ modulo }}"}</code> (inserzione/campagna/form).
            Verranno sostituiti al volo per ciascun lead.
          </div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={12}
            className="w-full border-2 border-[#0A0A0A] p-3 text-sm resize-none font-mono"
            data-testid="wa-template-editor"
          />
          <div className="flex gap-2 justify-between items-center flex-wrap">
            {defaultText && (
              <button
                onClick={() => setText(defaultText)}
                className="text-xs underline text-[#0A0A0A]/60 hover:text-[#FF5A1F]"
                data-testid="wa-reset-default"
              >
                ↻ Ripristina default
              </button>
            )}
            <div className="flex gap-2 ml-auto">
              <button
                onClick={onClose}
                className="px-3 py-2 border-2 border-[#0A0A0A] bg-white text-xs uppercase tracking-widest font-bold hover:bg-neutral-50"
              >
                Annulla
              </button>
              <button
                onClick={save}
                disabled={saving || !text.trim()}
                className="px-4 py-2 border-2 border-emerald-700 bg-emerald-700 text-white text-xs uppercase tracking-widest font-bold hover:bg-emerald-800 disabled:opacity-50"
                data-testid="wa-template-save"
              >
                {saving ? <Loader2 size={14} className="animate-spin inline" /> : "💾 Salva"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helper tiles for summary panel
// ---------------------------------------------------------------------------
function SummaryTile({ label, value, color = "neutral", sub, testid }) {
  const palette = {
    neutral: "border-[#0A0A0A]/40 bg-[#F2F2EC] text-[#0A0A0A]",
    sky:     "border-sky-600 bg-sky-50 text-sky-700",
    emerald: "border-emerald-600 bg-emerald-50 text-emerald-700",
    orange:  "border-[#FF5A1F] bg-orange-50 text-[#FF5A1F]",
    violet:  "border-violet-600 bg-violet-50 text-violet-700",
    red:     "border-red-600 bg-red-50 text-red-700",
  }[color];
  return (
    <div className={`border-2 p-3 ${palette}`} data-testid={testid}>
      <div className="text-[9px] uppercase tracking-widest font-bold opacity-70">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
      {sub && <div className="text-[9px] uppercase tracking-widest opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

function MiniTile({ label, value }) {
  return (
    <div className="border border-[#0A0A0A]/15 bg-white px-2 py-2">
      <div className="text-[9px] uppercase tracking-widest font-bold text-[#0A0A0A]/50">{label}</div>
      <div className="font-display text-xl text-[#0A0A0A] mt-0.5">{value}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// RecallPlannerModal — pianificatore "Da richiamare" (data+ora+nota)
// ---------------------------------------------------------------------------
function RecallPlannerModal({ lead, onClose, onConfirm }) {
  // Default sensato: domani alle 10:00
  const initDate = React.useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
    return d;
  }, []);
  const toLocalInputValue = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const [when, setWhen] = React.useState(toLocalInputValue(initDate));
  const [note, setNote] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const presets = [
    { label: "Tra 1 ora", mins: 60 },
    { label: "Oggi pomeriggio (16:00)", today: 16 },
    { label: "Domani 10:00", tomorrow: 10 },
    { label: "Dopodomani 10:00", in2days: 10 },
  ];
  const applyPreset = (p) => {
    const d = new Date();
    if (p.mins) {
      d.setMinutes(d.getMinutes() + p.mins);
    } else if (typeof p.today === "number") {
      d.setHours(p.today, 0, 0, 0);
    } else if (typeof p.tomorrow === "number") {
      d.setDate(d.getDate() + 1); d.setHours(p.tomorrow, 0, 0, 0);
    } else if (typeof p.in2days === "number") {
      d.setDate(d.getDate() + 2); d.setHours(p.in2days, 0, 0, 0);
    }
    setWhen(toLocalInputValue(d));
  };
  const confirm = async () => {
    if (!when) { toast.error("Seleziona data e ora"); return; }
    setSaving(true);
    try {
      // Converte in ISO UTC
      const iso = new Date(when).toISOString();
      await onConfirm(iso, note.trim());
    } finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-start justify-center p-4 z-50 overflow-y-auto" onClick={onClose}>
      <div className="bg-[#FAFAF7] border-4 border-violet-600 max-w-xl w-full my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-violet-600 bg-violet-600 text-white">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/70">Pianifica richiamo</div>
            <div className="font-display text-2xl uppercase">⏰ {lead.artist_name}</div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="text-xs text-[#0A0A0A]/70">
            Pianifica il richiamo di <b>{lead.artist_name}</b>. Il lead resterà nella lista con lo stato
            <span className="font-bold text-violet-700"> "Richiama dopo"</span> e vedrai l'orario sulla riga.
          </div>
          <div className="flex gap-2 flex-wrap">
            {presets.map((p, i) => (
              <button key={i} onClick={() => applyPreset(p)}
                className="px-2 py-1 border-2 border-violet-600 bg-white text-violet-700 text-[10px] uppercase tracking-widest font-bold hover:bg-violet-600 hover:text-white"
                data-testid={`recall-preset-${i}`}>
                {p.label}
              </button>
            ))}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Data e ora</label>
            <input
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm"
              data-testid="recall-when"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Nota (facoltativa)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="es. 'Richiamare dopo pranzo, mi ha detto che era in riunione'"
              className="mt-1 w-full border-2 border-[#0A0A0A] p-2 text-sm resize-none"
              data-testid="recall-note"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={onClose} className="px-3 py-2 border-2 border-[#0A0A0A] bg-white text-xs uppercase tracking-widest font-bold hover:bg-neutral-50">
              Annulla
            </button>
            <button onClick={confirm} disabled={saving}
              className="px-4 py-2 border-2 border-violet-600 bg-violet-600 text-white text-xs uppercase tracking-widest font-bold hover:bg-violet-700 disabled:opacity-50"
              data-testid="recall-confirm">
              {saving ? <Loader2 size={14} className="animate-spin inline" /> : <>⏰ Pianifica richiamo</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
